import blobStream from 'blob-stream';
import _ from 'underscore';

import {assert} from 'Asserts';
import {isNodeTesting} from 'lib/Browser';
import {CDRDefinition} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import {BLUE_DARKER, GREEN_LIGHT} from 'ui-library/Colors';
import {capitalize, ellipsize} from 'util/NameUtil';

import {
  AlignmentPages,
  Block,
  CDRAnnotationName,
  Highlight,
  LETTER,
  Line,
  Page,
  PageSize,
  PDFExportCDRAnnotation,
  PDFExportRawSequence,
  PDFExportRawSequences,
  ResidueHighlightFormat,
  Underline,
} from './AlignmentPDFConstants';

export const PDF_EXPORT_DEFAULT_LINES_PER_PAGE = 50;
export const NUMBER_GAP_LINES_BETWEEN_BLOCKS = 3;
export const GAP_CHARACTERS_BETWEEN_NAME_AND_RESIDUES = 1;
export const DEFAULT_MAX_NAME_CHARS_TO_DISPLAY = 12;
export const SEPARATOR_LINE_CHARACTER = '·';
// One for the page number + one gap line
const STATIC_LINES_PER_PAGE = 2;
// One line for residue indices
const STATIC_LINES_PER_BLOCK = 1;

export function getDefaultResiduesPerLine(pageFormat: PageSize): number {
  return (
    pageFormat.charactersPerLine -
    GAP_CHARACTERS_BETWEEN_NAME_AND_RESIDUES -
    DEFAULT_MAX_NAME_CHARS_TO_DISPLAY
  );
}

/**
 * Calculate the number of name characters and residues per line,
 */
function getNameLength(
  rawSequences: PDFExportRawSequences,
  charactersPerLine: number,
  residuesPerLine: number
): number {
  assert(residuesPerLine < charactersPerLine, 'Cannot specify more residues than characters');
  const longestNameLength = Math.max(...rawSequences.map((seq) => seq.sequenceName.length));
  const maxNameLength = charactersPerLine - GAP_CHARACTERS_BETWEEN_NAME_AND_RESIDUES - residuesPerLine;
  return Math.min(maxNameLength, longestNameLength);
}

/**
 * Either truncate or pad the name to be the given length.
 */
function formatNameToUniformLength(name: string, length: number): string {
  if (name.length > length) {
    return ellipsize(name, length);
  } else if (name.length < length) {
    return name + ' '.repeat(length - name.length);
  }
  return name;
}

function generateRowFromSequence(
  sequence: PDFExportRawSequence,
  nameLength: number,
  startingResidueIndex: number,
  endingResidueIndex: number
): PDFExportRawSequence {
  return {
    sequenceName: formatNameToUniformLength(sequence.sequenceName, nameLength),
    residues: sequence.residues.slice(startingResidueIndex, endingResidueIndex),
    cdrAnnotations: sequence.cdrAnnotations
      ? sequence.cdrAnnotations.filter(
          (ann) => ann.range.start < endingResidueIndex || ann.range.end > startingResidueIndex
        )
      : [],
  };
}

// Colors were picked for PDF visibility and are not intended for
// reuse in other parts of the platform
export const residueHighlightColors: {[key in ResidueHighlightFormat]: string} = {
  [ResidueHighlightFormat.MUTATION]: '#ee7f7b',
  [ResidueHighlightFormat.UNKNOWN]: '#ffd635',
};

export const annotationUnderlineColors: {[key in CDRAnnotationName]: string} = {
  [CDRAnnotationName.FRAMEWORK]: GREEN_LIGHT,
  [CDRAnnotationName.CDR]: BLUE_DARKER,
};

interface Range {
  start: number;
  inclusiveEnd: number;
}

/**
 * Generate the (1-indexed) index line for each block based on where in the alignment the block is.
 * If both indices don't fit over the residues, leave off the end index:
 * 48   54
 * GATTACA
 *
 * or
 *
 * 48
 * GAT
 */
function generateIndexLine(zeroIndexedRange: Range, numPreResidueCharacters: number): Line {
  const oneIndexedStart = zeroIndexedRange.start + 1;
  const oneIndexedEnd = zeroIndexedRange.inclusiveEnd + 1;
  const numResiduesPerLine = oneIndexedEnd - oneIndexedStart + 1;
  const numSpacesBetweenIndices =
    numResiduesPerLine - (oneIndexedStart.toString().length + oneIndexedEnd.toString().length);
  let line = ' '.repeat(numPreResidueCharacters) + oneIndexedStart.toString();
  if (numSpacesBetweenIndices >= 0) {
    line += ' '.repeat(numSpacesBetweenIndices) + oneIndexedEnd.toString();
  }
  return {text: line, highlights: [], underlines: []};
}

// Find next index in array matching predicate
function findNextIndex<T>(arr: Array<T>, startingIndex: number, predicate: (t: T) => boolean): number {
  for (let i = startingIndex; i < arr.length; i++) {
    if (predicate(arr[i])) {
      return i;
    }
  }

  return -1;
}

/**
 * Create the highlight ranges for a single row, indicating which color to add at which index.
 * Indices are offset by a given number to account for the name before the residues start.
 */
function generateMismatchHighlights(row: PDFExportRawSequence, highlightOffset: number): Array<Highlight> {
  const colors = row.residues.map((residue) =>
    residue.format ? residueHighlightColors[residue.format] : null
  );
  const highlights: Array<Highlight> = [];
  let index = 0;
  while (index < colors.length) {
    const color = colors[index];
    if (!color) {
      index++;
      continue;
    }
    const highlight: Highlight = {color, index: index + highlightOffset};
    const nextNonMatchingIndex = findNextIndex(colors, index + 1, (c) => c !== color);
    if (nextNonMatchingIndex === -1) {
      // highlight goes till the end of the line
      highlight.length = colors.length - index;
      highlights.push(highlight);
      break;
    } else {
      highlight.length = nextNonMatchingIndex - index;
      highlights.push(highlight);
      index = nextNonMatchingIndex;
    }
  }

  return highlights;
}

function generateCDRAnnotationUnderlines(
  row: PDFExportRawSequence,
  highlightOffset: number,
  range: {start: number; end: number}
): Array<Underline> {
  if (!row.cdrAnnotations) {
    return [];
  }
  const anns = _.compact(
    row.cdrAnnotations.map((ann) => {
      if (ann.range.start >= range.end || ann.range.end < range.start) {
        return null;
      }
      const annStart = Math.max(ann.range.start, range.start);
      const annLength = Math.min(ann.range.end, range.end) - annStart;
      return {
        color: annotationUnderlineColors[ann.name],
        index: highlightOffset + annStart - range.start,
        length: annLength,
      };
    })
  );
  return anns;
}

/**
 * Convert a block to the lines of text that will be rendered in the PDF.
 * Conditionally includes block separators. charactersPerLine lets the gap lines be padded
 * to the correct length even if it's the last line so the residues are cut short.
 * Assumes all sequences are the same length.
 */
function convertBlockToLines(
  block: Block,
  {
    charactersPerLine,
    includeBlockSeparationLines,
    showMismatches,
    nameLength,
    zeroIndexedRange,
    selectionStart,
  }: {
    charactersPerLine: number;
    includeBlockSeparationLines: boolean;
    showMismatches: boolean;
    nameLength: number;
    zeroIndexedRange: Range;
    selectionStart: number;
  }
): Array<Line> {
  const indexLine = generateIndexLine(
    {
      start: zeroIndexedRange.start + selectionStart,
      inclusiveEnd: zeroIndexedRange.inclusiveEnd + selectionStart,
    },
    nameLength + GAP_CHARACTERS_BETWEEN_NAME_AND_RESIDUES
  );
  const highlightOffset = nameLength + GAP_CHARACTERS_BETWEEN_NAME_AND_RESIDUES;
  const lines = [
    indexLine,
    ...block.rows.map(
      (row: PDFExportRawSequence): Line => ({
        text: `${row.sequenceName} ${row.residues.map((r) => r.letter).join('')}`,
        highlights: showMismatches ? generateMismatchHighlights(row, highlightOffset) : [],
        underlines: generateCDRAnnotationUnderlines(row, highlightOffset, {
          start: zeroIndexedRange.start,
          end: zeroIndexedRange.inclusiveEnd + 1,
        }),
      })
    ),
  ];

  if (includeBlockSeparationLines) {
    lines.push({text: ' ', highlights: [], underlines: []});
    lines.push({text: SEPARATOR_LINE_CHARACTER.repeat(charactersPerLine), highlights: [], underlines: []});
    lines.push({text: ' ', highlights: [], underlines: []});
  }

  return lines;
}

function getTitleLines(
  index: number,
  alignmentName: string,
  cdrDefinition: CDRDefinition | null,
  pageSize: PageSize
): Array<Line> {
  const title = `Page ${index} ${alignmentName}${
    cdrDefinition ? `, CDR Definition: ${capitalize(cdrDefinition)}` : ''
  }`;
  const text =
    title.length > pageSize.charactersPerLine ? ellipsize(title, pageSize.charactersPerLine) : title;
  return [
    {text, highlights: [], underlines: []},
    {text: ' ', highlights: [], underlines: []},
  ];
}

export interface AlignmentPDFRenderingOptions {
  pageSize: PageSize;
  showMismatches: boolean;
  residuesPerLine: number;
  selectionStart: number;
  selectionEnd: number | null;
}

const defaultOptions: AlignmentPDFRenderingOptions = {
  pageSize: LETTER,
  showMismatches: true,
  residuesPerLine: getDefaultResiduesPerLine(LETTER),
  selectionStart: 0,
  selectionEnd: null,
};

/**
 * Generate pages for segments that must be split across multiple pages
 */
function getPagesForBigSegments(
  rawSequences: PDFExportRawSequences,
  options: AlignmentPDFRenderingOptions,
  numSegments: number,
  residuesPerSequence: number,
  sequenceLinesPerPage: number,
  nameLength: number,
  alignmentName: string,
  cdrDefinition: CDRDefinition | null
): AlignmentPages {
  const numPagesPerSegment = Math.ceil(rawSequences.length / sequenceLinesPerPage);
  const pagesOfBlocks: Array<Page> = [];

  // Iterate through segments and add pages so we get a flattened pages array.
  for (const segmentIndex of _.range(numSegments)) {
    const startingResidueIndex = segmentIndex * options.residuesPerLine;
    const endingResidueIndexExclusive = Math.min(
      startingResidueIndex + options.residuesPerLine,
      residuesPerSequence
    );
    for (const pageIndex of _.range(numPagesPerSegment)) {
      const startingLineIndex = pageIndex * sequenceLinesPerPage;
      const endingLineIndex = (pageIndex + 1) * sequenceLinesPerPage;

      const block = {
        rows: rawSequences
          .slice(startingLineIndex, endingLineIndex)
          .map((seq) =>
            generateRowFromSequence(seq, nameLength, startingResidueIndex, endingResidueIndexExclusive)
          ),
      };

      // In this mode, we only ever have one block per page
      // and we don't do block separation lines
      pagesOfBlocks.push({
        lines: [
          ...getTitleLines(pagesOfBlocks.length + 1, alignmentName, cdrDefinition, options.pageSize),
          ...convertBlockToLines(block, {
            charactersPerLine: options.pageSize.charactersPerLine,
            includeBlockSeparationLines: false,
            showMismatches: options.showMismatches,
            nameLength,
            zeroIndexedRange: {
              start: startingResidueIndex,
              inclusiveEnd: endingResidueIndexExclusive - 1,
            },
            selectionStart: options.selectionStart,
          }),
        ],
      });
    }
  }

  return {pages: pagesOfBlocks};
}

/**
 * Generate pages for segments that can fit on a single page
 */
function getPagesForSmallSegments(
  rawSequences: PDFExportRawSequences,
  options: AlignmentPDFRenderingOptions,
  numSegments: number,
  residuesPerSequence: number,
  nameLength: number,
  alignmentName: string,
  cdrDefinition: CDRDefinition | null
): AlignmentPages {
  // Here, segments are smaller than a page so we calculate how many segments/page we can fit.
  const linesInBlock = rawSequences.length + NUMBER_GAP_LINES_BETWEEN_BLOCKS + STATIC_LINES_PER_BLOCK;
  // We can always fit one per page
  const segmentsPerPage = Math.max(
    Math.floor((options.pageSize.linesPerPage - STATIC_LINES_PER_PAGE) / linesInBlock),
    1
  );
  const numPages = Math.ceil(numSegments / segmentsPerPage);

  return {
    pages: _.range(numPages).map((pageIndex) => {
      const linesInPage: Array<Line> = getTitleLines(
        pageIndex + 1,
        alignmentName,
        cdrDefinition,
        options.pageSize
      );
      for (const segmentIndex of _.range(segmentsPerPage)) {
        const startingResidueIndex = (pageIndex * segmentsPerPage + segmentIndex) * options.residuesPerLine;
        const endingResidueIndexExclusive = Math.min(
          startingResidueIndex + options.residuesPerLine,
          residuesPerSequence
        );
        if (startingResidueIndex >= residuesPerSequence) {
          // Means we're done with actual residues but are still iterating
          // through the rest of the potential blocks on the page
          continue;
        }

        const block = {
          rows: rawSequences.map((seq) =>
            generateRowFromSequence(seq, nameLength, startingResidueIndex, endingResidueIndexExclusive)
          ),
        };
        linesInPage.push(
          ...convertBlockToLines(block, {
            charactersPerLine: options.pageSize.charactersPerLine,
            includeBlockSeparationLines: true,
            showMismatches: options.showMismatches,
            nameLength,
            zeroIndexedRange: {
              start: startingResidueIndex,
              inclusiveEnd: endingResidueIndexExclusive - 1,
            },
            selectionStart: options.selectionStart,
          })
        );
      }
      return {lines: linesInPage};
    }),
  };
}

/**
 * Remove annotations outside of the selection, and adjust indices of remaining annotations to match new
 * selected indices.
 */
function adjustedCDRAnnotations(
  rawSequence: PDFExportRawSequence,
  startIndex: number
): Array<PDFExportCDRAnnotation> {
  if (!rawSequence.cdrAnnotations) {
    return [];
  }
  return rawSequence.cdrAnnotations.map((ann) => ({
    ...ann,
    range: {
      start: ann.range.start - startIndex,
      end: ann.range.end - startIndex,
    },
  }));
}

function getSelection(
  rawSequences: PDFExportRawSequences,
  startIndex: number,
  endIndex: number | null
): PDFExportRawSequences {
  // Handles when we use end === 0 to mean the end
  const end = endIndex == null || endIndex === 0 ? rawSequences[0].residues.length : endIndex;

  return rawSequences.map((sequence) => ({
    ...sequence,
    residues: sequence.residues.slice(startIndex, end),
    cdrAnnotations: adjustedCDRAnnotations(sequence, startIndex),
  }));
}

/**
 * Return alignment data formatted for feeding to pdfkit to render in blocks, bucketed by page.
 *
 * Definitions:
 * - segment: a section of an alignment including all of its sequences across a range of residues. A segment
 *   may span multiple pages if there are too many sequences to fit on the page
 * - block: a portion of a segment that is rendered on the page. A page may have many blocks, and a block
 *   never takes up more than a page.
 *
 * Page creation is different if a segment spans multiple pages than if it doesn't:
 * - If a segment spans multiple pages, we iterate through the segments since there's only one block per page,
 *   creating all the pages for a single segment and adding them to the array of pages.
 * - If a segment is smaller than a page, we iterate through pages, adding as many blocks as will fit on a
 *   page before moving to the next page.
 */
export function alignmentToPages(
  rawSequences: PDFExportRawSequences,
  alignmentName: string,
  cdrDefinition: CDRDefinition | null,
  optionOverrides?: Partial<AlignmentPDFRenderingOptions>
): AlignmentPages {
  const options = {
    ...defaultOptions,
    ...optionOverrides,
  };
  assert(
    // eslint-disable-next-line no-restricted-syntax
    _.uniq(rawSequences.map((seq) => seq.residues.length)).length === 1,
    'Cannot generate PDF for alignment with different length sequences'
  );

  const sequenceSelections = getSelection(rawSequences, options.selectionStart, options.selectionEnd);

  const residuesPerSequence = sequenceSelections[0].residues.length;
  const nameLength = getNameLength(
    sequenceSelections,
    options.pageSize.charactersPerLine,
    options.residuesPerLine
  );
  const numSegments = Math.ceil(residuesPerSequence / options.residuesPerLine);

  // To calculate whether we'll have to split segments across pages, we account for
  // the static rows per page and per block, since there will be at least one block per page.
  const sequenceLinesPerPage = options.pageSize.linesPerPage - STATIC_LINES_PER_PAGE - STATIC_LINES_PER_BLOCK;
  const segmentsSpanMultiplePages = sequenceSelections.length > sequenceLinesPerPage;

  if (segmentsSpanMultiplePages) {
    return getPagesForBigSegments(
      sequenceSelections,
      options,
      numSegments,
      residuesPerSequence,
      sequenceLinesPerPage,
      nameLength,
      alignmentName,
      cdrDefinition
    );
  }

  return getPagesForSmallSegments(
    sequenceSelections,
    options,
    numSegments,
    residuesPerSequence,
    nameLength,
    alignmentName,
    cdrDefinition
  );
}

/**
 * Render AlignmentPages to an in-memory PDF using pdfkit. Should have as little logic as possible;
 * leaving most logic to generating AlignmentPages.
 * @param pages: POJO describing the alignment to be rendered
 * @param onFinish: callback for getting the URL.createObjectURL of the PDF once it's done rendering
 */
export function renderPagesToPDF(
  pages: AlignmentPages,
  pageSize: PageSize,
  onFinish: (blobURL: string) => void
): void {
  import(/* webpackChunkName: "pdfkit" */ 'pdfkit').then(({default: PDFDocument}) => {
    const doc = new PDFDocument({autoFirstPage: false});
    const stream = doc.pipe(blobStream());

    doc.fontSize(10);
    doc.font('Courier');

    const characterWidth = doc.widthOfString(' ');
    const characterHeight = doc.currentLineHeight();

    pages.pages.forEach((page) => {
      doc.addPage({margin: 20, layout: pageSize === LETTER ? 'portrait' : 'landscape'});
      page.lines.forEach((line) => {
        for (const highlight of line.highlights) {
          doc.highlight(
            doc.x + characterWidth * highlight.index,
            doc.y,
            characterWidth * (highlight.length || 1),
            // Leave space at the bottom for underlines
            characterHeight * 0.8,
            {
              color: highlight.color,
            }
          );
        }
        for (const underline of line.underlines) {
          doc.underline(
            doc.x + characterWidth * underline.index,
            doc.y,
            characterWidth * (underline.length || 1),
            characterHeight,
            {
              color: underline.color,
            }
          );
        }
        doc.text(line.text);
      });
    });

    doc.end();

    stream.on('finish', () => {
      // toBlobURL only works in the browser, not node, so skip during node testing
      if (!isNodeTesting) {
        onFinish(stream.toBlobURL('application/pdf'));
      }
    });
  });
}

/**
 * Process raw alignment data into pages, and renders those pages into a PDF
 */
export default function generateAlignmentPDF(
  rawSequences: PDFExportRawSequences,
  alignmentName: string,
  cdrDefinition: CDRDefinition | null,
  overrides: Partial<AlignmentPDFRenderingOptions>,
  onFinish: (blobURL: string) => void
): void {
  const alignmentPages = alignmentToPages(rawSequences, alignmentName, cdrDefinition, overrides);
  renderPagesToPDF(alignmentPages, overrides.pageSize || defaultOptions.pageSize, onFinish);
}
